import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { supabase, _rawSupabase, setReadOnly, getReadOnly } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import type { OrphanReason } from '../store/authStore'
import type { Tenant } from '../types'
import { PAGE_METAS } from '../lib/permissions/pageMetaIndex'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rawDb = (t: string): any => (_rawSupabase as any).from(t)

/** Wrap a promise with a timeout — rejects if it takes longer than `ms` */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Query timed out after ${ms}ms`)), ms),
    ),
  ])
}

async function syncPagePermissions(tenantId: string): Promise<void> {
  // Once-per-tenant-per-day guard so we don't hit the RPC on every page load.
  const today = new Date().toISOString().slice(0, 10)
  const cacheKey = `palstro-page-sync:${tenantId}`
  try {
    if (localStorage.getItem(cacheKey) === today) return
  } catch {
    // localStorage may be unavailable in some envs — fall through and sync.
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)('sync_page_permissions', {
      p_tenant_id: tenantId,
      p_pages: PAGE_METAS,
    })
    if (error) {
      console.warn('[syncPagePermissions]', error.message)
      return
    }
    if (typeof data === 'number' && data > 0) {
      console.log(`[syncPagePermissions] inserted ${data} new permission row(s)`)
    }
    try { localStorage.setItem(cacheKey, today) } catch { /* noop */ }
  } catch (e) {
    console.warn('[syncPagePermissions]', e instanceof Error ? e.message : e)
  }
}

async function checkSubscriptionStatus(tenantId: string): Promise<void> {
  try {
    const { data: sub } = await rawDb('subscriptions')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    console.log('[subscription] current_period_end:', sub?.current_period_end)

    const wasReadOnly = getReadOnly()
    const isExpired =
      !!sub &&
      sub.status === 'trialing' &&
      new Date(sub.current_period_end) < new Date()

    setReadOnly(isExpired)
    useAuthStore.getState().setIsReadOnly(isExpired)
    useAuthStore.getState().setSubscription(
      sub ? { status: sub.status, current_period_end: sub.current_period_end } : null,
    )

    if (isExpired && !wasReadOnly) {
      toast("Your trial has expired. You're in read-only mode.", { icon: '⚠️' })
    }
  } catch (e) {
    console.error('[checkSubscriptionStatus]', e instanceof Error ? e.message : e)
  }
}

async function checkOnlineStoreStatus(tenantId: string): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)('get_online_store_status', {
      p_tenant_id: tenantId,
    })
    if (error) {
      console.warn('[checkOnlineStoreStatus]', error.message)
      return
    }
    useAuthStore.getState().setOnlineStore(data ?? null)
  } catch (e) {
    console.warn('[checkOnlineStoreStatus]', e instanceof Error ? e.message : e)
  }
}

async function loadTenantForUser(userId: string): Promise<void> {
  const { setTenant, setRole, setTenantResolved, setOrphanReason } = useAuthStore.getState()
  try {
    const query = db('tenant_users')
      .select('role, tenant_id, tenants(*)')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at')
      .limit(1)
      .maybeSingle()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: membership, error } = await withTimeout<any>(query, 8000)

    if (error) {
      // Transient DB/network error — do NOT classify as orphan. Leave
      // orphanReason null so the guard falls back to the login redirect.
      console.error('[loadTenantForUser] DB error:', error.message)
      setTenant(null)
      setRole(null)
      setOrphanReason(null)
      setTenantResolved(true)
      return
    }

    if (!membership) {
      // Authenticated but no ACTIVE membership. Distinguish "never linked to a
      // workspace" from "membership deactivated" so the fallback page can show
      // the right message.
      let reason: OrphanReason = 'no_workspace'
      try {
        const { data: anyRows } = await db('tenant_users')
          .select('id')
          .eq('user_id', userId)
          .limit(1)
        if (Array.isArray(anyRows) && anyRows.length > 0) reason = 'deactivated'
      } catch {
        // If the follow-up lookup fails, fall back to no_workspace.
      }
      setTenant(null)
      setRole(null)
      setOrphanReason(reason)
      setTenantResolved(true)
      return
    }

    const raw = (membership as { tenants: unknown }).tenants as Record<string, unknown>
    setTenant(raw as unknown as Tenant)
    setRole(membership.role)

    await checkSubscriptionStatus(raw.id as string)
    checkOnlineStoreStatus(raw.id as string)
    syncPagePermissions(raw.id as string)
  } catch (e) {
    console.error('[loadTenantForUser] Error:', e instanceof Error ? e.message : e)
    setTenant(null)
    setRole(null)
    setOrphanReason(null)
    setTenantResolved(true)
  }
}

// `enabled` lets the caller skip staff auth-init entirely (default on). The public
// storefront (subdomain / custom domain) is an anon CUSTOMER surface with its own
// isolated customerClient — running staff auth there did nothing useful but kept a
// 60s subscription / online-store poll alive behind the shop. On a flaky connection
// that poll threw on a loop and flooded the error reporter (Sentry) sitting under a
// live storefront, so we turn it off on those hosts.
export function useAuthInit(enabled: boolean = true) {
  const { setUser, clearSession, setInitializing, setForcePasswordChange } = useAuthStore()

  useEffect(() => {
    if (!enabled) {
      // Storefront host: nothing to bootstrap. Clear the initializing flag so any
      // non-storefront fallback render never hangs on a spinner, then bail — no
      // getSession, no onAuthStateChange subscription, no polling interval.
      setInitializing(false)
      return
    }

    // Bootstrap session on app load
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email ?? '',
          full_name: session.user.user_metadata?.full_name ?? session.user.email ?? '',
          avatar_url: session.user.user_metadata?.avatar_url,
        })
        setForcePasswordChange(!!session.user.user_metadata?.force_password_change)
        const { tenant, setTenantResolved } = useAuthStore.getState()
        if (!tenant) {
          loadTenantForUser(session.user.id)
        } else {
          // Tenant is cached from localStorage — treat membership as resolved
          // and still re-check subscription from DB.
          setTenantResolved(true)
          checkSubscriptionStatus(tenant.id)
          checkOnlineStoreStatus(tenant.id)
          syncPagePermissions(tenant.id)
        }
      } else {
        clearSession()
      }
      setInitializing(false)
    })

    // IMPORTANT: Do NOT make Supabase DB calls inside onAuthStateChange — the
    // Supabase JS v2 client holds an internal lock during this callback, and any
    // subsequent Supabase call will wait for that lock → deadlock → infinite spin.
    // Tenant loading is handled explicitly by each page (LoginPage, etc.) after
    // the auth call resolves.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email ?? '',
          full_name: session.user.user_metadata?.full_name ?? session.user.email ?? '',
          avatar_url: session.user.user_metadata?.avatar_url,
        })
        setForcePasswordChange(!!session.user.user_metadata?.force_password_change)
        // For token refresh or external sign-in (not the normal login page flow),
        // defer the tenant load outside the lock with setTimeout.
        if (event === 'TOKEN_REFRESHED') {
          const { tenant } = useAuthStore.getState()
          if (!tenant) {
            setTimeout(() => loadTenantForUser(session.user.id), 0)
          }
        }
      } else if (event === 'SIGNED_OUT') {
        clearSession()
      }
    })

    // Poll every 60 seconds to catch real-time trial expiry
    const pollInterval = setInterval(() => {
      const { tenant } = useAuthStore.getState()
      if (tenant) {
        checkSubscriptionStatus(tenant.id)
        checkOnlineStoreStatus(tenant.id)
      }
    }, 60_000)

    return () => {
      subscription.unsubscribe()
      clearInterval(pollInterval)
    }
  }, [enabled])
}

export { loadTenantForUser, checkOnlineStoreStatus }
